import { Empty, Radio, Select, Space, Spin, Tag, Typography } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import type {
  NocStationDetail,
  StationOperationReason,
  StationOperationStatus,
  StationRegistryStatus,
} from '@goelectric/shared-types';
import { darkColors, semanticColors, useThemeMode } from '@goelectric/ui-components';
import { ConnectorStateBadge } from '@/features/noc/components/StatusBadge';
import { StationOperationBadge, StationRegistryBadge } from '@/features/cargadores/components/StationBadges';

/**
 * Opción del selector del laboratorio: unión de lo registrado (equipos) y lo que
 * sólo existe en el core (estaciones de simulación por defecto). `registered`
 * distingue el flujo de alta del camino negativo.
 */
export interface LabStation {
  id: string;
  name: string | null;
  protocol: string | null;
  vendor?: string | null;
  model?: string | null;
  registered: boolean;
  registryStatus: StationRegistryStatus | null;
  operationStatus: StationOperationStatus | null;
  operationReason: StationOperationReason | null;
  isOnline: boolean;
}

interface StationPanelProps {
  stations: LabStation[];
  selectedStationId: string | null;
  onSelectStation: (stationId: string) => void;
  selectedConnectorId: number | null;
  onSelectConnector: (connectorId: number) => void;
  detail: NocStationDetail | null;
  loading: boolean;
}

/** Selector de estación y conector con su estado en vivo. */
export function StationPanel({
  stations,
  selectedStationId,
  onSelectStation,
  selectedConnectorId,
  onSelectConnector,
  detail,
  loading,
}: StationPanelProps) {
  const selectedStation = stations.find((station) => station.id === selectedStationId) ?? null;
  // El oro de marca no pasa AA como texto sobre el fondo warning; en claro se usa
  // la variante oscurecida y en oscuro el ámbar del tema.
  const { resolvedTheme } = useThemeMode();
  const warningText =
    resolvedTheme === 'dark' ? darkColors.warning : semanticColors.warningText;
  const connectors = detail?.connectorDetails ?? [];
  const activeConnector =
    connectors.find((connector) => connector.connectorId === selectedConnectorId) ?? connectors[0] ?? null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Select
        style={{ width: '100%' }}
        placeholder="Selecciona una estación"
        value={selectedStationId ?? undefined}
        onChange={onSelectStation}
        showSearch
        optionFilterProp="value"
        options={stations.map((station) => ({
          value: station.id,
          label: `${station.id} · ${station.name ?? 'sin nombre'}${
            station.registered ? '' : ' · sin registro'
          }`,
        }))}
        aria-label="Estación"
      />

      {selectedStation && (
        <Space wrap size="small">
          {selectedStation.registered ? (
            <>
              <StationRegistryBadge status={selectedStation.registryStatus ?? 'PRE_REGISTERED'} />
              <StationOperationBadge
                status={selectedStation.operationStatus}
                reason={selectedStation.operationReason}
              />
            </>
          ) : (
            <Tag color="warning" style={{ color: warningText }}>
              Sin registro en Cargadores
            </Tag>
          )}
          <Tag>Protocolo: {selectedStation.protocol ?? '—'}</Tag>
          <Tag>
            {selectedStation.vendor ?? '—'} {selectedStation.model ? `· ${selectedStation.model}` : ''}
          </Tag>
          {!selectedStation.isOnline && (
            <Typography.Text type="secondary">Sin heartbeat reciente</Typography.Text>
          )}
        </Space>
      )}

      {selectedStationId && !detail && loading && (
        <div style={{ display: 'grid', placeItems: 'center', padding: 16 }}>
          <Spin tip="Leyendo estación…">
            <div style={{ padding: 16 }} />
          </Spin>
        </div>
      )}

      {selectedStationId && detail && connectors.length === 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="La estación no reporta conectores" />
      )}

      {connectors.length > 0 && (
        <Radio.Group
          value={activeConnector?.connectorId}
          onChange={(event) => onSelectConnector(Number(event.target.value))}
          optionType="button"
          buttonStyle="solid"
          aria-label="Conector"
        >
          {connectors.map((connector) => (
            <Radio.Button key={connector.connectorId} value={connector.connectorId}>
              Conector {connector.connectorId}
            </Radio.Button>
          ))}
        </Radio.Group>
      )}

      {activeConnector && (
        <Space wrap size="small">
          <ConnectorStateBadge state={activeConnector.state} />
          <Tag color={activeConnector.live ? 'green' : 'default'} icon={<ThunderboltOutlined />}>
            {activeConnector.live ? 'Live (Redis)' : 'Sin muestra en vivo'}
          </Tag>
          <Typography.Text type="secondary">
            raw: {activeConnector.stateRaw ?? '—'} · {activeConnector.errorCode ?? '—'}
          </Typography.Text>
        </Space>
      )}
    </Space>
  );
}
